import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, getApiErrorMessage, getValidationErrors } from '@/lib/validationErrors';
import { useEnumValueOptions } from '@/lib/enumGridFilters';
import { planScenarioService } from '@/services/finance/PlanScenarioService';
import { planVersionService } from '@/services/finance/PlanVersionService';
import { fiscalYearService } from '@/services/finance/FiscalYearService';

interface CopiedScenario {
  id: string;
  name: string;
}

interface CopyPlanScenarioDialogProps {
  /** The SOURCE plan scenario to copy from. */
  entityId: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful copy (the hub uses it to re-evaluate). */
  onCopied?: (newScenario: CopiedScenario) => void;
}

/**
 * Start the new year: POST /api/finance/planscenario/{id}/copyPlanScenario — a new scenario for a
 * chosen fiscal year plus its first DRAFT version, carrying the header structure of a chosen source
 * version (never values). Omitting the source version lets the server pick the adopted structure
 * (latest once-published, else the highest version number).
 * Spec: docs/15-designspecs/CopyPlanScenario.md.
 */
export function CopyPlanScenarioDialog({ entityId, open, onClose, onCopied }: CopyPlanScenarioDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [targetFiscalYearId, setTargetFiscalYearId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [scenarioType, setScenarioType] = useState('');
  const [sourceVersionId, setSourceVersionId] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [mode, setMode] = useState<'full' | 'variant'>('full');
  const [created, setCreated] = useState<CopiedScenario | null>(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  // One key per dialog session: a network retry of the same attempt replays instead of copying twice.
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  const { options: typeOptions } = useEnumValueOptions('PlanScenarioType');

  const { data: sourceScenario } = useQuery({
    queryKey: ['plan-scenario', entityId],
    queryFn: () => planScenarioService.getById(entityId),
    enabled: open,
  });
  const { data: fiscalYears } = useQuery({
    queryKey: ['fiscal-years-for-copy'],
    queryFn: async () =>
      (await fiscalYearService.getList({ skipCount: 0, maxResultCount: 200, sorting: 'endDate desc' })).items,
    enabled: open,
  });
  const { data: sourceVersions } = useQuery({
    queryKey: ['plan-versions-of-scenario', entityId],
    queryFn: async () =>
      (await planVersionService.getList({
        skipCount: 0, maxResultCount: 200, sorting: 'versionNo desc', planScenarioId: entityId,
      })).items,
    enabled: open,
  });

  const copyMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<CopiedScenario>(
        `/api/finance/planscenario/${entityId}/copyPlanScenario`,
        {
          targetFiscalYearId,
          name: name.trim(),
          code: code.trim(),
          scenarioType: scenarioType || null,
          sourceVersionId: sourceVersionId || null,
          versionLabel: versionLabel.trim() || null,
          oneHeaderPerVariant: mode === 'variant',
        },
        { headers: { 'X-Idempotency-Key': idempotencyKeyRef.current } },
      );
      return response.data;
    },
    onSuccess: (scenario) => {
      setCreated(scenario);
      setApiValidationErrors([]);
      onCopied?.(scenario);
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
        return;
      }
      const message = getApiErrorMessage(error)
        ?? t('common.genericError', { defaultValue: 'An error occurred' });
      setApiValidationErrors([{ field: 'form', message, instancePath: '#', members: [] }]);
    },
  });

  const handleClose = () => {
    setCreated(null);
    setTargetFiscalYearId('');
    setName('');
    setCode('');
    setScenarioType('');
    setSourceVersionId('');
    setVersionLabel('');
    setMode('full');
    setApiValidationErrors([]);
    idempotencyKeyRef.current = crypto.randomUUID();
    onClose();
  };

  const canSubmit = !!targetFiscalYearId && !!name.trim() && !!code.trim() && !copyMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('Finance.CopyPlanScenario.Title', { defaultValue: 'New scenario (copy structure)' })}
      </DialogTitle>
      <DialogContent>
        <DialogValidationErrors
          errors={apiValidationErrors}
          onClear={() => setApiValidationErrors([])}
        />

        {created ? (
          <Alert
            severity="success"
            action={
              <Button
                size="small"
                onClick={() => { handleClose(); navigate(`/finance/planscenarios/${created.id}`); }}
              >
                {t('Finance.CopyPlanScenario.OpenNew', { defaultValue: 'Open scenario' })}
              </Button>
            }
          >
            {t('Finance.CopyPlanScenario.Success', {
              defaultValue: '"{{name}}" created with a first draft version — structure copied, no values.',
              name: created.name,
            })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('Finance.CopyPlanScenario.Intro', {
                defaultValue: 'Creates a new scenario for a chosen fiscal year, from "{{source}}". The first draft version carries the header structure, owners and capacities — values never follow.',
                source: sourceScenario?.name ?? '…',
              })}
            </Typography>
            <TextField
              select
              fullWidth
              required
              label={t('Finance.CopyPlanScenario.FiscalYear', { defaultValue: 'Fiscal year (the new planning horizon)' })}
              value={targetFiscalYearId}
              onChange={(e) => setTargetFiscalYearId(e.target.value)}
              sx={{ mb: 2 }}
            >
              {(fiscalYears ?? []).map((y) => (
                <MenuItem key={y.id} value={y.id}>{y.name} ({y.code})</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              label={t('Finance.CopyPlanScenario.Name', { defaultValue: 'Name (e.g. Budget 2027)' })}
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              required
              label={t('Finance.CopyPlanScenario.Code', { defaultValue: 'Code — unique within the legal entity' })}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              fullWidth
              label={t('Finance.CopyPlanScenario.Type', { defaultValue: 'Scenario type' })}
              value={scenarioType}
              onChange={(e) => setScenarioType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">
                <em>
                  {t('Finance.CopyPlanScenario.TypeInherit', {
                    defaultValue: 'Same as the source ({{type}})',
                    type: sourceScenario?.scenarioType ?? '…',
                  })}
                </em>
              </MenuItem>
              {typeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              fullWidth
              label={t('Finance.CopyPlanScenario.SourceVersion', { defaultValue: 'Copy structure from' })}
              value={sourceVersionId}
              onChange={(e) => setSourceVersionId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">
                <em>
                  {t('Finance.CopyPlanScenario.SourceVersionAuto', {
                    defaultValue: 'Automatic — latest published, otherwise the latest version',
                  })}
                </em>
              </MenuItem>
              {(sourceVersions ?? []).map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {`V${v.versionNo}${v.label ? ` · ${v.label}` : ''} (${v.status})`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={t('Finance.CopyPlanScenario.VersionLabel', {
                defaultValue: 'Label for the first draft (optional — defaults to V1)',
              })}
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControl>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'full' | 'variant')}>
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={t('Finance.CopyPlanVersion.ModeFull', {
                    defaultValue: 'All headers — the full structure for the whole org tree',
                  })}
                />
                <FormControlLabel
                  value="variant"
                  control={<Radio />}
                  label={t('Finance.CopyPlanVersion.ModeVariant', {
                    defaultValue: 'One header per header type and plan view — a skeleton to fan out afterwards',
                  })}
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {created
            ? t('common.close', { defaultValue: 'Close' })
            : t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {!created && (
          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={() => copyMutation.mutate()}
          >
            {t('Finance.CopyPlanScenario.Confirm', { defaultValue: 'Create scenario' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
